/**
 * @format
 * @module lib/notifications/website-alerts
 */
import { local as store, session as sessionStore, isLocalStorageTypeSupported } from "lib/store";
import tgamConfig from "config/tgam";
import { mark, measure } from "lib/performance/user-timing";
import logger from "utils/logger";
import { API_PB_RENDER_FEATURE } from "lib/gamservices";
import { isMobile } from "utils/detect";
import { getDataAttrValFromStr, qs, qsa, showCookieAlert } from "utils/utils";
import { validStatus, handleJSON } from "utils/fetch";
import { websiteAlertDismissListener, websiteAlertNodeSelector } from "ui/templates/notifications";

// Key for local storage item that keeps track of website alerts that should
// no longer be shown. For mobile view, alerts should no longer be shown if
// it's a subsequent pageview within the same session so it may be removed from
// this list.
const storeNS = "tgam.notifications.websiteAlerts";

// Key for local storage item that keeps track of website alerts that have
// been dismissed.
const storeDismissed = "tgam.notifications.websiteAlertsDismissed";

// Key for local storage item that keeps track of number of times
// user/device/browser combination has visited the site.
const storeNumVisited = "tgam.notifications.numVisited";

// Key for session storage item that's used for detecting if user already
// started a new "session" and had first pageview. Opening a new tab/window
// begins a new session (in terms of session storage's definition of a session).
const storeIsNewSession = "tgam.notifications.websiteAlertsIsNewSession";

let isNewSession = null;

const isPbAdmin = tgamConfig.env.isPbAdmin;
const debugTag = "[WEBSITE-ALERT]";

const WebsiteAlert = {
  init(rendering) {
    const hasId = /data\-alertid=\"([A-Za-z0-9-]+)\"/gim.exec(rendering);
    this.id = hasId ? hasId[1] : null;
    this.enabled = isPbAdmin ? true : /data-enabled="true"/.test(rendering);
    this.html = rendering.replace(/(\r\n|\n|\r|\t)/gm, "");
  },
  hasBeenSeen() {
    if (isPbAdmin) {
      return false;
    }
    const db = store.get(storeNS);

    return (
      isLocalStorageTypeSupported("localStorage") &&
      db &&
      typeof db.indexOf === "function" &&
      db.indexOf(this.id) !== -1
    );
  }
};

/**
 * Records website alert with alertId as being seen.
 * @param {String} alertId
 */
function setAsSeen(alertId) {
  const db = store.get(storeNS);
  let newData = undefined;
  if (Array.isArray(db) && !db.includes(alertId)) {
    // The alert with alertId is not already in the array of alerts that should
    // no longer be shown so add it.
    db.unshift(alertId);
    newData = [...db];
  } else {
    newData = [alertId];
  }
  store.set(storeNS, newData);
}

/**
 * Removes website alert with alertId from seen local storage item.
 * @param {String} alertId
 */
function removeSeen(alertId) {
  let newData;

  const db = store.get(storeNS);

  if (Array.isArray(db)) {
    newData = db.filter(alertItemId => {
      return alertItemId !== alertId;
    });

    store.set(storeNS, newData);
  }
}

/**
 * Records website alert with alertId as being dismissed to local storage.
 * @param {String} alertId
 */
function setAsDismissed(alertId) {
  const db = store.get(storeDismissed);
  let newData;

  if (Array.isArray(db) && !db.includes(alertId)) {
    db.unshift(alertId);
    newData = [...db];
  } else {
    newData = [alertId];
  }
  store.set(storeDismissed, newData);
}

/**
 * Returns whether or not alert with alertId has been dismissed.
 * @param {String} alertId
 * @returns {Boolean} whether or not alert has been dismissed
 */
function hasBeenDismissed(alertId) {
  if (isPbAdmin) {
    return false;
  }

  const db = store.get(storeDismissed);

  return (
    isLocalStorageTypeSupported("localStorage") &&
    db &&
    typeof db.indexOf === "function" &&
    db.indexOf(alertId) !== -1
  );
}

/**
 * Processes and displays website alert.
 * @param {Object} node - website alert node
 * @param {String} featureRendering - rendered feature for website alert node
 */
function parse(node, featureRendering) {
  const alert = Object.create(WebsiteAlert);
  alert.init(featureRendering);
  const isEmpty = alert.html.indexOf("js-c-website-alert") === -1;

  logger.groupCollapsed(`${debugTag}`);
  logger.info(`${debugTag} enabled: ${alert.enabled}`);
  logger.info(`${debugTag} seen: ${alert.hasBeenSeen()}`);
  logger.info(`${debugTag} empty: ${isEmpty}`);
  logger.groupEnd(`${debugTag}`);

  if (!node || !alert.enabled || alert.hasBeenSeen() || isEmpty) {
    return;
  }
  node.classList.add(`u-space-mt${node.dataset.umt}`);
  node.classList.add(`u-space-mb${node.dataset.umb}`);
  node.insertAdjacentHTML("afterbegin", alert.html);
  node.classList.remove("u-hidden");
}

/**
 * Shows the cookies alert message when cookies are disabled.
 */
function cookieAlert() {
  if (!showCookieAlert()) {
    return;
  }
  const cookieAlert = qs(".c-website-alert--cookies");
  const cookieAlertDismiss = qs(".c-website-alert--cookies .c-website-alert__dismiss-btn");
  if (cookieAlert && showCookieAlert) {
    cookieAlert.classList.remove("u-hidden");
    cookieAlertDismiss.setAttribute(
      "data-sophi-track",
      "{'event':'internal_promotion','internal_promotion':{'action': ['served','visible'], 'feature': 'cookies disabled message'}}"
    );
  }
}

/**
 * Enforces session limit on alert with alertId. For mobile view, ensures
 * that all subsequent pageviews within the same session will not show alert
 * again. Also takes into account whether the alert has already has been dismissed.
 * @param {String} alertId - the alert ID of the alert to enforce the session limit
 * @param {Number} sessionLimit - max number of sessions the user should see the alert
 * @param {Number} numVisitedStoreKey - key of local storage item that keeps track of visits
 * @param {Boolean} isMobileView - whether or not user is on a mobile viewport width
 */
function enforceSessionLimit(alertId, sessionLimit, numVisitedStoreKey, isMobileView) {
  // Number of visits/sessions that have already occurred for alert with alertId.
  // (It might be the case that not all alerts show up on all pages or have the same
  // start date on the site so there may be a discrepancy between the number of
  // visits for different alerts).
  let numVisited = store.get(numVisitedStoreKey);

  if (!numVisited) {
    // First time the user is visiting the site.
    numVisited = 0;
  }

  if (isNewSession) {
    // We want to be able to identify if a user is on a subsequent pageview
    // of the same session.
    numVisited++;
    sessionStore.set(storeIsNewSession, false);
    store.set(numVisitedStoreKey, numVisited);
  }

  // Handles mobile alert display logic.
  if (isMobileView) {
    logger.info(`${debugTag} isMobileView true`);

    if (!isNewSession) {
      // Don't show the alert again on subsequent pageviews
      // within the same session.
      setAsSeen(alertId);
    } else if (isNewSession && numVisited <= sessionLimit && !hasBeenDismissed(alertId)) {
      // If it's a new visit and the session limit threshold has not been exceeded,
      // we want to still show the alert message so remove it from the list of alerts
      // to not show, just in case it was added in a previous session (to handle
      // the subsequent pageview with the same session requirement).
      removeSeen(alertId);
    }
  }

  if (numVisited === sessionLimit + 1) {
    // Note: we need to add this flag right when the limit has been exceeded
    // as oppose to adding the flag when numVisited === sessionLimit after the
    // parse() call due to the requirement of showing the alert at the threshold
    // limit on all pageviews on desktop breakpoints.
    logger.info(`${debugTag} Session limit has been exceeded.  Alert id ${alertId}`);

    // Ensure that the alert is not shown on subsequent visits since it has
    // just met the session limit threshold.
    setAsSeen(alertId);
  }
}

/**
 * Initializes website alerts.
 */
export default function broadcast() {
  mark("website_alerts_started");
  cookieAlert();
  const alerts = qsa(websiteAlertNodeSelector);

  if (!alerts) {
    mark("website_alerts_done");
    measure("website_alerts");
    return;
  }

  const isMobileView = isMobile();

  // If the session storage item doesn't exist then it means that it's the
  // first page view of the new session.
  isNewSession = !sessionStore.has(storeIsNewSession);
  logger.info(`${debugTag} isNewSession: `, isNewSession);

  for (let i = 0, len = alerts.length; i < len; i++) {
    const alert = alerts[i];
    const url = API_PB_RENDER_FEATURE + alert.dataset.source;
    fetch(url)
      .then(validStatus)
      .then(handleJSON)
      .then(data => {
        const node = qs(`${websiteAlertNodeSelector}[data-source="${alert.dataset.source}"]`);

        // We need to use regex here (as opposed to qs()) to determine the session
        // limit settings on the website alert since it is not added to the DOM
        // until parse() is called.
        const sessionLimitEnabled = /data-limitenabled="true"/.test(data.rendering);

        if (sessionLimitEnabled) {
          logger.info(`${debugTag} Successfully found sessionLimitEnabled`, sessionLimitEnabled);

          // Note: we need to escape do an an extra '\' to actually include the \
          // as part of the regex.
          const alertId = getDataAttrValFromStr(data.rendering, "data-alertid", "([\\w|-]+)");
          logger.info(`${debugTag} Successfully found alertId`, alertId);

          const sessionLimit = Number(
            getDataAttrValFromStr(data.rendering, "data-limit", "([0-9]+)")
          );

          const numVisitedStoreKey = `${storeNumVisited}-${alertId}`;
          enforceSessionLimit(alertId, sessionLimit, numVisitedStoreKey, isMobileView);
        }

        parse(node, data.rendering);
      })
      .catch(() => {});
  }

  if (alerts.length) {
    const container = alerts[0].closest(".pb-container");
    websiteAlertDismissListener(container, ".js-c-website-alert__dismiss-btn", id => {
      setAsDismissed(id);
      setAsSeen(id);
    });
  }

  mark("website_alerts_done");
  measure("website_alerts");
}
